import { useCallback, useEffect, useMemo, useState } from 'react';
import { decodeGame, encodeGame, readHashCode } from '@/game/codec';
import {
  applyMove,
  createEmptyState,
  createRematch,
  generateGameId,
  withGuestName,
} from '@/game/engine';
import type { CellIndex, GameState, Mark } from '@/game/types';
import { useI18n } from '@/i18n';
import { GameBoard } from '@/features/GameBoard';
import { ShareButton } from '@/features/ShareButton';
import { TopBar } from '@/features/TopBar';
import {
  getGameRecord,
  isStaleLink,
  resolveRoleFromNames,
  saveGameRecord,
  touchMoveCount,
} from '@/shared/storage';

type ViewMode = 'home' | 'game' | 'invalid' | 'stale';

function writeHash(state: GameState) {
  window.history.replaceState(null, '', `#${encodeGame(state)}`);
}

function clearHash() {
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

export function AppShell() {
  const { t } = useI18n();
  const [mode, setMode] = useState<ViewMode>('home');
  const [state, setState] = useState<GameState | null>(null);
  const [role, setRole] = useState<Mark | null>(null);
  const [homeNick, setHomeNick] = useState('');
  const [guestNick, setGuestNick] = useState('');
  const [awaitingFirstMove, setAwaitingFirstMove] = useState(false);
  const [homeError, setHomeError] = useState<string | null>(null);
  const [guestError, setGuestError] = useState<string | null>(null);

  const syncFromHash = useCallback(() => {
    const code = readHashCode(window.location.hash);
    if (!code) {
      setMode('home');
      setState(null);
      setRole(null);
      setAwaitingFirstMove(false);
      return;
    }

    try {
      const decoded = decodeGame(code);
      if (isStaleLink(decoded.id, decoded.moves.length)) {
        setState(decoded);
        setMode('stale');
        return;
      }

      const record = getGameRecord(decoded.id);
      const resolved = resolveRoleFromNames(
        decoded.nameX,
        decoded.nameO,
        record?.nick,
        record?.role,
      );

      touchMoveCount(decoded.id, decoded.moves.length, resolved ?? undefined, record?.nick);
      setState(decoded);
      setRole(resolved);
      setAwaitingFirstMove(false);
      setMode('game');
    } catch {
      setMode('invalid');
      setState(null);
      setRole(null);
    }
  }, []);

  useEffect(() => {
    syncFromHash();
    const onHashChange = () => syncFromHash();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [syncFromHash]);

  const needsGuestName = Boolean(state && role === 'O' && !state.nameO);
  const canPlay = Boolean(
    state &&
      role &&
      state.status === 'playing' &&
      state.turn === role &&
      (role === 'X' || state.nameO) &&
      !needsGuestName,
  );

  const statusMessage = useMemo(() => {
    if (!state || !role) return null;
    if (state.status === 'won') {
      const name = state.winner === 'X' ? state.nameX : state.nameO;
      return t('winner', { name: name || state.winner || '' });
    }
    if (state.status === 'draw') return t('draw');
    if (needsGuestName) return t('guestNicknameTitle');
    if (awaitingFirstMove && state.moves.length === 0) return t('makeFirstMove');
    if (state.turn === role) return t('yourTurn', { mark: role });
    const waitingName =
      state.turn === 'X'
        ? state.nameX || t('waitingOpponentName')
        : state.nameO || t('waitingOpponentName');
    return t('waitShare', { name: waitingName });
  }, [awaitingFirstMove, needsGuestName, role, state, t]);

  function goHome() {
    clearHash();
    setMode('home');
    setState(null);
    setRole(null);
    setAwaitingFirstMove(false);
    setHomeError(null);
    setGuestError(null);
  }

  function beginLocalGame() {
    const nick = homeNick.trim();
    if (!nick) {
      setHomeError(t('needNickname'));
      return;
    }
    setHomeError(null);
    const id = generateGameId();
    const next = createEmptyState({ id, nameX: nick, starter: 'X' });
    saveGameRecord(id, { role: 'X', moveCount: 0, nick });
    setState(next);
    setRole('X');
    setAwaitingFirstMove(true);
    setMode('game');
  }

  function commitMove(cell: CellIndex) {
    if (!state || !role || !canPlay) return;
    try {
      const next = applyMove(state, cell);
      touchMoveCount(next.id, next.moves.length, role, getGameRecord(next.id)?.nick);
      setState(next);
      setAwaitingFirstMove(false);
      writeHash(next);
    } catch {
      // ignore illegal clicks
    }
  }

  function claimGuestRole() {
    if (!state) return;
    const nick = guestNick.trim();
    if (!nick) {
      setGuestError(t('needNickname'));
      return;
    }

    if (nick === state.nameX) {
      saveGameRecord(state.id, { role: 'X', moveCount: state.moves.length, nick });
      setRole('X');
      setGuestError(null);
      return;
    }

    if (state.nameO && nick === state.nameO) {
      saveGameRecord(state.id, { role: 'O', moveCount: state.moves.length, nick });
      setRole('O');
      setGuestError(null);
      return;
    }

    if (state.nameO) {
      // Names already set; fall back to O if this device has no prior role match
      saveGameRecord(state.id, { role: 'O', moveCount: state.moves.length, nick: state.nameO });
      setRole('O');
      setGuestError(null);
      return;
    }

    const next = withGuestName(state, nick);
    saveGameRecord(next.id, { role: 'O', moveCount: next.moves.length, nick });
    setRole('O');
    setState(next);
    writeHash(next);
    setGuestError(null);
  }

  function startRematch() {
    if (!state || !role) return;
    const next = createRematch(state, generateGameId());
    const nick = role === 'X' ? next.nameX : next.nameO;
    saveGameRecord(next.id, { role, moveCount: 0, nick });
    setState(next);
    setAwaitingFirstMove(next.turn === role);
    writeHash(next);
  }

  const showShare =
    state &&
    role &&
    state.status === 'playing' &&
    !awaitingFirstMove &&
    !needsGuestName &&
    state.turn !== role;

  const shareLabelKey =
    state && state.moves.length === 0
      ? 'shareChallenge'
      : state && state.moves.length <= 1
        ? 'shareChallenge'
        : 'shareTurn';
  const shareTextKey =
    state && state.moves.length === 0
      ? 'shareTextRematch'
      : state && state.moves.length <= 1
        ? 'shareTextChallenge'
        : 'shareTextTurn';

  return (
    <div className="mx-auto min-h-dvh w-full max-w-lg px-4 py-6 sm:px-6 sm:py-10">
      <TopBar />

      {mode === 'home' ? (
        <section className="fade-up space-y-6 rounded-[2rem] border border-vz-line/80 bg-vz-surface/75 p-6 shadow-xl shadow-teal-950/5 backdrop-blur">
          <div className="space-y-2">
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-vz-ink sm:text-5xl">
              {t('brand')}
            </h1>
            <p className="text-base font-semibold text-vz-muted">{t('tagline')}</p>
          </div>
          <label className="block space-y-2">
            <span className="text-sm font-bold text-vz-ink">{t('yourNickname')}</span>
            <input
              className="w-full rounded-2xl border-2 border-vz-line bg-vz-bg px-4 py-3 font-semibold text-vz-ink outline-none ring-vz-accent focus:ring-2"
              value={homeNick}
              maxLength={16}
              placeholder={t('nicknamePlaceholder')}
              onChange={(e) => setHomeNick(e.target.value)}
            />
          </label>
          {homeError ? <p className="text-sm font-bold text-vz-x">{homeError}</p> : null}
          <button
            type="button"
            className="w-full rounded-2xl bg-vz-accent px-4 py-3 font-display text-lg font-bold text-vz-accent-ink transition hover:brightness-110 active:scale-[0.98]"
            onClick={beginLocalGame}
          >
            {t('startGame')}
          </button>
        </section>
      ) : null}

      {mode === 'invalid' ? (
        <section className="fade-up space-y-4 rounded-[2rem] border border-vz-line bg-vz-surface/80 p-6">
          <p className="font-display text-2xl font-bold">{t('invalidLink')}</p>
          <button
            type="button"
            className="w-full rounded-2xl border-2 border-vz-line px-4 py-3 font-bold"
            onClick={goHome}
          >
            {t('backHome')}
          </button>
        </section>
      ) : null}

      {mode === 'stale' ? (
        <section className="fade-up space-y-4 rounded-[2rem] border border-vz-line bg-vz-surface/80 p-6">
          <p className="font-display text-2xl font-bold">{t('staleLink')}</p>
          <button
            type="button"
            className="w-full rounded-2xl border-2 border-vz-line px-4 py-3 font-bold"
            onClick={goHome}
          >
            {t('backHome')}
          </button>
        </section>
      ) : null}

      {mode === 'game' && state && !role ? (
        <section className="fade-up space-y-4 rounded-[2rem] border border-vz-line bg-vz-surface/80 p-6">
          <p className="font-display text-xl font-bold">{t('guestNicknameTitle')}</p>
          <p className="text-sm font-semibold text-vz-muted">
            {state.nameX} (X) {t('vs')} {state.nameO || 'O'}
          </p>
          <input
            className="w-full rounded-2xl border-2 border-vz-line bg-vz-bg px-4 py-3 font-semibold outline-none ring-vz-accent focus:ring-2"
            value={guestNick}
            maxLength={16}
            placeholder={t('nicknamePlaceholder')}
            onChange={(e) => setGuestNick(e.target.value)}
          />
          {guestError ? <p className="text-sm font-bold text-vz-x">{guestError}</p> : null}
          <button
            type="button"
            className="w-full rounded-2xl bg-vz-o px-4 py-3 font-display font-bold text-white"
            onClick={claimGuestRole}
          >
            {t('guestNicknameCta')}
          </button>
        </section>
      ) : null}

      {mode === 'game' && state && role ? (
        <section className="fade-up space-y-5">
          <div className="rounded-[2rem] border border-vz-line/80 bg-vz-surface/75 p-5 shadow-lg shadow-teal-950/5 backdrop-blur">
            <div className="mb-4 flex items-center justify-between gap-3 text-center">
              <div className="flex-1">
                <p className="font-display text-lg font-extrabold text-vz-x">
                  {state.nameX || 'X'}
                </p>
                <p className="text-xs font-bold uppercase tracking-wide text-vz-muted">X</p>
              </div>
              <p className="font-display text-sm font-bold text-vz-muted">{t('vs')}</p>
              <div className="flex-1">
                <p className="font-display text-lg font-extrabold text-vz-o">
                  {state.nameO || 'O'}
                </p>
                <p className="text-xs font-bold uppercase tracking-wide text-vz-muted">O</p>
              </div>
            </div>
            <p className="mb-2 text-center text-sm font-bold text-vz-muted">
              {t('playingAs', {
                name: role === 'X' ? state.nameX : state.nameO || role,
                mark: role,
              })}
            </p>
            <p className="mb-5 text-center font-display text-xl font-extrabold text-vz-ink">
              {statusMessage}
            </p>

            {needsGuestName ? (
              <div className="mb-5 space-y-3">
                <input
                  className="w-full rounded-2xl border-2 border-vz-line bg-vz-bg px-4 py-3 font-semibold outline-none ring-vz-accent focus:ring-2"
                  value={guestNick}
                  maxLength={16}
                  placeholder={t('nicknamePlaceholder')}
                  onChange={(e) => setGuestNick(e.target.value)}
                />
                {guestError ? <p className="text-sm font-bold text-vz-x">{guestError}</p> : null}
                <button
                  type="button"
                  className="w-full rounded-2xl bg-vz-o px-4 py-3 font-display font-bold text-white"
                  onClick={claimGuestRole}
                >
                  {t('guestNicknameCta')}
                </button>
              </div>
            ) : null}

            <GameBoard state={state} canPlay={canPlay} onPlay={commitMove} />
          </div>

          {showShare ? (
            <ShareButton state={state} labelKey={shareLabelKey} textKey={shareTextKey} />
          ) : null}

          {state.status !== 'playing' ? (
            <div className="space-y-3 rounded-[2rem] border border-vz-line bg-vz-surface/80 p-5">
              <ShareButton state={state} labelKey="shareResult" textKey="shareTextResult" />
              <button
                type="button"
                className="w-full rounded-2xl bg-vz-accent px-4 py-3 font-display font-bold text-vz-accent-ink"
                onClick={startRematch}
              >
                {t('rematch')}
              </button>
              <button
                type="button"
                className="w-full rounded-2xl border-2 border-vz-line px-4 py-3 font-bold"
                onClick={goHome}
              >
                {t('newGame')}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
